import React, { useState } from 'react'
import { Alert, Button, Col, Form, Row } from 'react-bootstrap'
import './ClassificationStep.css'
import SelectField from './SelectField'
import { useTranslation } from 'react-i18next'

const CLASSIFICATION_OPTIONS = [
  { value: 'small_medium_enterprise', label: 'SME' },
  { value: 'large_company', label: 'Large Company' },
  { value: 'rnd_intensive_company', label: 'R&D Intensive Company' },
]

const CLAIM_TYPE_OPTIONS = [
  { value: 'sme', label: 'SME' },
  { value: 'rdec', label: 'RDEC' },
  { value: 'sme_and_rdec', label: 'Both SME & RDEC' },
  { value: 'merge_type', label: 'MERGE' },
  { value: 'eris', label: 'ERIS' },
]

const STAFFING_COST_TYPE_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'rapid', label: 'Rapid' },
  { value: 'realtime', label: 'Realtime' },
]

const STAFFING_COST_AGGREGATION_OPTIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'function', label: 'Group' },
]

const COST_METHODOLOGY_CARDS = [
  {
    field: 'is_using_sampling_cost_methodology',
    label: 'Sampling',
    description: 'Use a statistical sample to estimate R&D costs across the population.',
  },
  {
    field: 'is_using_shortlisting_cost_methodology',
    label: 'Shortlisting',
    description: 'Identify and include only the most relevant R&D activities and costs.',
  },
  {
    field: 'is_using_rag_cost_methodology',
    label: 'RAG',
    description: 'Apply a Red / Amber / Green rating to classify cost eligibility.',
  },
]

const MANDATORY_FIELDS = [
  'rnd_tax_purposes_classification',
  'rate_type',
  'staffing_cost_type',
  'staffing_cost_aggregation_type',
]

export default function ClassificationStep({ claimGroup, onChange, onNext, onBack, questionnaires }) {
  const [showValidation, setShowValidation] = useState(false)
  const { t } = useTranslation();

  const getMissingMandatory = () => {
    return MANDATORY_FIELDS.filter(f => !claimGroup[f])
  }

  const isFieldInvalid = (field) => showValidation && !claimGroup[field]

  const handleNext = () => {
    if (getMissingMandatory().length > 0) {
      setShowValidation(true)
      return
    }
    onNext()
  }

  const handleChange = (e) => {
    onChange(e)
    if (showValidation && getMissingMandatory().filter(f => f !== e.target.name).length === 0) {
      setShowValidation(false)
    }
  }

  const toggleMethodology = (field) => {
    onChange({ target: { name: field, value: !(claimGroup[field] === true) } })
  }

  return (
    <div className="classification-step">
      <h5 className="classification-step__title">Step 2 — Classification & Methodology</h5>

      {showValidation && getMissingMandatory().length > 0 && (
        <Alert variant="danger" className="classification-step__alert">
          Please complete all required fields to continue.
        </Alert>
      )}

      <div className="classification-step__section">
        <SelectField
          label="Classification for R&D Tax Purposes"
          name="rnd_tax_purposes_classification"
          value={claimGroup.rnd_tax_purposes_classification || ''}
          options={CLASSIFICATION_OPTIONS}
          onChange={handleChange}
          invalid={isFieldInvalid('rnd_tax_purposes_classification')}
        />
        <SelectField
          label="Claim Type"
          name="rate_type"
          value={claimGroup.rate_type || ''}
          options={CLAIM_TYPE_OPTIONS}
          onChange={handleChange}
          invalid={isFieldInvalid('rate_type')}
        />
        <SelectField
          label={t("staffing_cost_type")}
          name="staffing_cost_type"
          value={claimGroup.staffing_cost_type || ''}
          options={STAFFING_COST_TYPE_OPTIONS}
          onChange={handleChange}
          invalid={isFieldInvalid('staffing_cost_type')}
        />
        <SelectField
          label="Staffing Cost Aggregation"
          name="staffing_cost_aggregation_type"
          value={claimGroup.staffing_cost_aggregation_type || ''}
          options={STAFFING_COST_AGGREGATION_OPTIONS}
          onChange={handleChange}
          invalid={isFieldInvalid('staffing_cost_aggregation_type')}
        />
      </div>

      <div className="classification-step__section">
        <Form.Group as={Row} className="mb-3">
          <Form.Label column md={5} className="classification-step__label">
            Questionnaire Template <span className="classification-step__optional-badge">Optional</span>
          </Form.Label>
          <Col md={7}>
            <Form.Control
              as="select"
              name="questionnaire_id"
              value={claimGroup.questionnaire_id || ''}
              onChange={onChange}
            >
              <option value=''>Select template...</option>
              {questionnaires?.map(q => (
                <option key={q.id} value={q.id}>{q.name}</option>
              ))}
            </Form.Control>
          </Col>
        </Form.Group>
      </div>

      <div className="classification-step__section">
        <div className="classification-step__section-label">
          Cost Methodology <span className="classification-step__optional-badge">Optional</span>
        </div>
        <div className="classification-step__cards-row">
          {COST_METHODOLOGY_CARDS.map(card => {
            const selected = !!claimGroup[card.field]
            return (
              <div
                key={card.field}
                className={`classification-step__card ${selected ? 'classification-step__card--selected' : ''}`}
                onClick={() => toggleMethodology(card.field)}
              >
                <div className="classification-step__card-title">{card.label}</div>
                <div className="classification-step__card-desc">{card.description}</div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="classification-step__footer">
        <Button variant="primary" onClick={onBack} className="classification-step__back-btn">
          ← Back
        </Button>
        <Button variant="primary" onClick={handleNext} className="classification-step__next-btn">
          Continue →
        </Button>
      </div>
    </div>
  )
}
